const NAMED_KEY_CODES = {
  Shift: 'ShiftLeft',
  Control: 'ControlLeft',
  Alt: 'AltLeft',
  Meta: 'MetaLeft',
  OS: 'MetaLeft',
};

/**
 * Resolve a physical key code from an event. `event.code` is the primary
 * source, with `event.key` as a fallback for synthetic events (automation) or
 * browsers where `code` is missing.
 */
export function resolveCode(event) {
  if (event.code) return event.code;

  const key = event.key;
  if (!key) return '';
  if (key === ' ') return 'Space';
  if (key.length === 1) {
    const upper = key.toUpperCase();
    if (upper >= 'A' && upper <= 'Z') return `Key${upper}`;
    if (upper >= '0' && upper <= '9') return `Digit${upper}`;
    return '';
  }

  return NAMED_KEY_CODES[key] ?? key;
}

/**
 * Keyboard, mouse and on-screen input with action bindings and pointer lock.
 *
 * Actions are consumed explicitly with `consumePressed()`, so a key press can
 * never fall between two fixed-update steps and get lost.
 */
export class Input {
  constructor(target, { bindings }) {
    this.target = target;
    this.bindings = bindings;
    this.actionsByCode = new Map();

    for (const [action, codes] of Object.entries(bindings)) {
      for (const code of codes) {
        const actions = this.actionsByCode.get(code) ?? [];
        actions.push(action);
        this.actionsByCode.set(code, actions);
      }
    }

    this.codesDown = new Set();
    // Actions held by on-screen controls. They never map to a key code, so
    // they are tracked separately and folded into the same queries.
    this.virtualDown = new Set();
    this.pressed = new Set();
    this.released = new Set();

    // Analog steering from the on-screen wheel. It overrides the discrete
    // keyboard axis while the wheel is held, then hands control back so the
    // keys keep working with auto-drive off.
    this.steerAnalog = 0;
    this.steerAnalogActive = false;

    // Auto-drive is a control scheme, not a physics mode: the vehicle still
    // reads the same axes, it just gets its throttle from a governor instead
    // of from the pedals. The gear is player intent, so it lives here.
    this.autoDrive = false;
    this.gear = 'D';

    // The auto-drive brake pedal. It is not the `back` action: that one doubles
    // as reverse, so holding it at a standstill would back the truck up. This
    // is a foot brake and only ever slows the truck down.
    this.brakeHeld = false;

    this.lookX = 0;
    this.lookY = 0;
    this.wheel = 0;
    this.rightDown = false;
    this.pointerLocked = false;

    this.onPointerLockChange = null;
    this.onPointerLockError = null;

    window.addEventListener('keydown', this.#onKeyDown);
    window.addEventListener('keyup', this.#onKeyUp);
    window.addEventListener('mousemove', this.#onMouseMove);
    window.addEventListener('mouseup', this.#onMouseUp);
    window.addEventListener('blur', this.#onWindowBlur);

    target.addEventListener('mousedown', this.#onMouseDown);
    target.addEventListener('contextmenu', this.#onContextMenu);
    target.addEventListener('wheel', this.#onWheel, { passive: false });

    document.addEventListener('pointerlockchange', this.#onPointerLockChange);
    document.addEventListener('pointerlockerror', this.#onPointerLockError);
  }

  isDown(action) {
    if (this.virtualDown.has(action)) return true;
    const codes = this.bindings[action];
    if (!codes) return false;
    return codes.some((code) => this.codesDown.has(code));
  }

  /** Steering demand: +1 full left, -1 full right. */
  get steerAxis() {
    if (this.steerAnalogActive) return this.steerAnalog;
    return (this.isDown('left') ? 1 : 0) - (this.isDown('right') ? 1 : 0);
  }

  /** Hold the wheel at an analog angle. +1 full left, -1 full right. */
  setSteer(value) {
    this.steerAnalog = Math.max(-1, Math.min(1, value));
    this.steerAnalogActive = true;
  }

  /** Let go of the wheel: the keyboard axis takes over again. */
  releaseSteer() {
    this.steerAnalog = 0;
    this.steerAnalogActive = false;
  }

  setGear(gear) {
    this.gear = gear === 'R' ? 'R' : 'D';
  }

  /** Hold or release the auto-drive brake pedal. */
  setBrake(down) {
    this.brakeHeld = down === true;
  }

  /** Throttle demand: +1 full throttle, -1 full brake/reverse. */
  get throttleAxis() {
    return (this.isDown('forward') ? 1 : 0) - (this.isDown('back') ? 1 : 0);
  }

  /**
   * Hold or release an action from an on-screen control. Mirrors the keyboard
   * path: a press is queued once, and the release only fires when neither the
   * key nor another on-screen button still holds the action.
   */
  setVirtual(action, down) {
    if (down) {
      if (this.virtualDown.has(action)) return;
      this.virtualDown.add(action);
      this.pressed.add(action);
      return;
    }

    if (!this.virtualDown.delete(action)) return;
    if (!this.#isActionDown(action)) this.released.add(action);
  }

  wasPressed(action) {
    return this.pressed.has(action);
  }

  consumePressed(action) {
    if (!this.pressed.has(action)) return false;
    this.pressed.delete(action);
    return true;
  }

  wasReleased(action) {
    return this.released.has(action);
  }

  consumeReleased(action) {
    if (!this.released.has(action)) return false;
    this.released.delete(action);
    return true;
  }

  /** Accumulated mouse movement since the last call. */
  takeLookDelta() {
    const delta = { x: this.lookX, y: this.lookY };
    this.lookX = 0;
    this.lookY = 0;
    return delta;
  }

  takeWheel() {
    const delta = this.wheel;
    this.wheel = 0;
    return delta;
  }

  clearTransient() {
    this.lookX = 0;
    this.lookY = 0;
    this.wheel = 0;
    this.pressed.clear();
    this.released.clear();
    // On-screen holds must not survive a pause or a lost pointer lock.
    this.virtualDown.clear();
    // A held wheel must not stay turned through a pause either.
    this.releaseSteer();
    this.brakeHeld = false;
  }

  requestPointerLock() {
    if (!this.target.requestPointerLock) {
      this.onPointerLockError?.();
      return;
    }

    try {
      const result = this.target.requestPointerLock();
      if (result?.catch) {
        result.catch(() => this.onPointerLockError?.());
      }
    } catch {
      this.onPointerLockError?.();
    }
  }

  exitPointerLock() {
    if (document.pointerLockElement === this.target) {
      document.exitPointerLock();
    }
  }

  dispose() {
    window.removeEventListener('keydown', this.#onKeyDown);
    window.removeEventListener('keyup', this.#onKeyUp);
    window.removeEventListener('mousemove', this.#onMouseMove);
    window.removeEventListener('mouseup', this.#onMouseUp);
    window.removeEventListener('blur', this.#onWindowBlur);

    this.target.removeEventListener('mousedown', this.#onMouseDown);
    this.target.removeEventListener('contextmenu', this.#onContextMenu);
    this.target.removeEventListener('wheel', this.#onWheel);

    document.removeEventListener('pointerlockchange', this.#onPointerLockChange);
    document.removeEventListener('pointerlockerror', this.#onPointerLockError);

    this.exitPointerLock();
  }

  #setActionDown(action) {
    this.pressed.add(action);
  }

  #setActionUp(action) {
    if (this.#isActionDown(action)) return;
    // Keep the queued press: a tap that starts and ends between two fixed
    // steps must still be consumed by the next one.
    this.released.add(action);
  }

  #isActionDown(action) {
    if (this.virtualDown.has(action)) return true;
    return this.bindings[action]?.some((code) => this.codesDown.has(code)) ?? false;
  }

  #isInteractiveTarget(target) {
    const tag = target?.tagName;
    return (
      tag === 'BUTTON' ||
      tag === 'INPUT' ||
      tag === 'TEXTAREA' ||
      tag === 'SELECT' ||
      tag === 'A' ||
      target?.isContentEditable === true
    );
  }

  #onKeyDown = (event) => {
    const code = resolveCode(event);
    const actions = this.actionsByCode.get(code);
    if (!actions) return;

    // Let focused UI controls handle their own keyboard interaction.
    if (this.#isInteractiveTarget(event.target)) return;

    event.preventDefault();
    if (event.repeat) return;

    this.codesDown.add(code);
    for (const action of actions) this.#setActionDown(action);
  };

  #onKeyUp = (event) => {
    const code = resolveCode(event);
    const actions = this.actionsByCode.get(code);
    if (!actions) return;

    // Don't swallow the keyup that activates a focused menu button (Space).
    if (this.#isInteractiveTarget(event.target)) return;

    event.preventDefault();
    this.codesDown.delete(code);

    for (const action of actions) this.#setActionUp(action);
  };

  #onMouseDown = (event) => {
    if (event.button === 2) this.rightDown = true;

    // A left click on the view claims the mouse for looking around. The game no
    // longer locks on start, so the on-screen controls stay clickable until the
    // player asks for mouse look.
    if (event.button === 0 && !this.pointerLocked) this.requestPointerLock();

    const code = `Mouse${event.button}`;
    const actions = this.actionsByCode.get(code);
    if (!actions) return;

    event.preventDefault();
    this.codesDown.add(code);
    for (const action of actions) this.#setActionDown(action);
  };

  #onMouseUp = (event) => {
    if (event.button === 2) this.rightDown = false;

    const code = `Mouse${event.button}`;
    const actions = this.actionsByCode.get(code);
    if (!actions) return;

    this.codesDown.delete(code);
    for (const action of actions) this.#setActionUp(action);
  };

  #onMouseMove = (event) => {
    // Pointer lock is the primary path. Right-drag is a fallback for browsers
    // or embedded contexts where pointer lock is unavailable.
    if (!this.pointerLocked && !this.rightDown) return;
    this.lookX += event.movementX ?? 0;
    this.lookY += event.movementY ?? 0;
  };

  #onWheel = (event) => {
    event.preventDefault();
    this.wheel += event.deltaY;
  };

  #onContextMenu = (event) => {
    event.preventDefault();
  };

  #onWindowBlur = () => {
    this.codesDown.clear();
    // A held on-screen button never gets its pointerup once the window loses
    // focus, so drop those holds too or the truck keeps driving.
    this.virtualDown.clear();
    this.pressed.clear();
    this.released.clear();
    this.releaseSteer();
    this.brakeHeld = false;
    this.rightDown = false;
  };

  #onPointerLockChange = () => {
    const locked = document.pointerLockElement === this.target;
    if (locked === this.pointerLocked) return;

    this.pointerLocked = locked;
    this.clearTransient();
    this.onPointerLockChange?.(locked);
  };

  #onPointerLockError = () => {
    this.onPointerLockError?.();
  };
}
